import React, { useState } from 'react';
import { NavLink, Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Menu, X, ChevronDown, Zap, Search
} from 'lucide-react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { PROJECTS, type ProjectConfig } from '../config/docs-nav';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const ProjectSelector: React.FC<{ activeProject: ProjectConfig }> = ({ activeProject }) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <div className="relative mb-8 px-1">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3.5 rounded-xl bg-white border border-slate-200/60 shadow-sm hover:shadow-md hover:border-slate-300/80 transition-all group relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-slate-50/5 to-slate-100/10 opacity-0 group-hover:opacity-100 transition-opacity" />
        <div className="flex items-center gap-3 relative z-10">
          <div 
            className="w-9 h-9 rounded-lg flex items-center justify-center text-white shadow-lg"
            style={{ backgroundColor: activeProject.color, boxShadow: `0 8px 16px -4px ${activeProject.color}40` }}
          >
            <activeProject.icon size={20} fill="currentColor" />
          </div>
          <div className="text-left">
            <div className="text-sm font-extrabold tracking-tight text-slate-900">{activeProject.name}</div>
            <div className="flex items-center gap-1.5 mt-1">
               <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
               <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">v1.2.4 stable</div>
            </div>
          </div>
        </div>
        <ChevronDown size={14} className={cn("text-slate-400 transition-transform relative z-10", isOpen && "rotate-180")} />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute top-full left-1 right-1 mt-2 z-50 bg-white border border-slate-200 rounded-2xl py-3 shadow-2xl animate-in fade-in slide-in-from-top-2 duration-200 ring-1 ring-black/5 overflow-hidden">
            <div className="px-5 py-2 mb-2 text-[10px] font-extrabold uppercase tracking-[0.2em] text-slate-500 border-b border-slate-50">Products</div>
            <div className="max-h-[300px] overflow-y-auto px-2 space-y-1">
              {PROJECTS.map((project) => (
                <button
                  key={project.id}
                  onClick={() => {
                    navigate(`/${project.id}`);
                    setIsOpen(false);
                  }}
                  className={cn(
                    "w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all group",
                    activeProject.id === project.id ? "bg-slate-50" : "hover:bg-slate-50"
                  )}
                >
                  <div 
                    className="w-7 h-7 rounded-lg flex items-center justify-center text-white shadow-sm"
                    style={{ backgroundColor: project.color }}
                  >
                    <project.icon size={14} fill="currentColor" />
                  </div>
                  <div className="flex flex-col items-start translate-y-[1px]">
                    <span className={cn(
                      "text-sm font-bold tracking-tight",
                      activeProject.id === project.id ? "text-slate-900" : "text-slate-600 group-hover:text-slate-900"
                    )}>
                      {project.name}
                    </span>
                  </div>
                  {activeProject.id === project.id && (
                    <div className="ml-auto w-1.5 h-1.5 rounded-full shadow-[0_0_8px_rgba(255,107,0,0.4)]" style={{ backgroundColor: project.color }} />
                  )}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export const MainLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  // Determine active project based on URL path
  const projectId = location.pathname.split('/')[1] || 'collector';
  const activeProject = PROJECTS.find(p => p.id === projectId) || PROJECTS[0];

  return (
    <div className="min-h-screen flex flex-col bg-white text-foreground antialiased selection:bg-primary/20 selection:text-primary transition-colors duration-500">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-md h-16 px-6 flex items-center justify-between border-b border-slate-200/60 shadow-sm transition-all">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => setSidebarOpen(!isSidebarOpen)}
            className="lg:hidden p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            {isSidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
          <Link to="/" className="flex items-center gap-2.5 group">
            <div className="w-8 h-8 rounded-lg bg-slate-900 flex items-center justify-center group-hover:scale-105 transition-transform shadow-lg shadow-black/10">
              <Zap className="text-white" size={18} fill="currentColor" />
            </div>
            <span className="text-xl font-black tracking-tighter text-slate-900 uppercase">Cyxor <span className="text-primary tracking-normal font-bold lowercase">hub</span></span>
          </Link>
          <div className="h-5 w-px bg-slate-200 mx-2 hidden sm:block" />
          <nav className="hidden sm:flex items-center gap-1">
             <button className="text-sm font-bold text-slate-900 px-4 py-2 rounded-lg hover:bg-slate-100 transition-colors">Docs</button>
             <button className="text-sm font-semibold text-slate-500 px-4 py-2 rounded-lg hover:bg-slate-100 transition-colors">API</button>
             <button className="text-sm font-semibold text-slate-500 px-4 py-2 rounded-lg hover:bg-slate-100 transition-colors">Pricing</button>
          </nav>
        </div>
        
        <div className="flex items-center gap-4">
          <button className="hidden md:flex items-center gap-3 px-4 py-2 rounded-xl border border-slate-200 bg-slate-50/50 hover:bg-slate-100/80 text-sm text-slate-500 transition-all group">
            <Search size={14} className="group-hover:text-primary transition-colors" />
            <span>Search docs...</span>
            <kbd className="hidden lg:inline-flex px-1.5 py-0.5 rounded-md border border-slate-200 bg-white shadow-sm items-center gap-1 select-none">
              <span className="text-[10px] font-sans font-bold">⌘</span>K
            </kbd>
          </button>
          <Link 
            to="#" 
            className="bg-slate-900 hover:bg-slate-800 text-white px-6 py-2.5 rounded-xl text-sm font-bold transition-all shadow-xl shadow-black/10 active:scale-95 flex items-center gap-2 border border-slate-800"
          >
            Sign In
          </Link>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar */}
        <aside className={cn(
          "fixed inset-y-0 left-0 z-40 w-72 bg-slate-50/50 backdrop-blur-md lg:bg-slate-50/30 lg:relative lg:translate-x-0 transition-transform duration-300 transform border-r border-slate-200/60",
          isSidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}>
          <div className="h-full overflow-y-auto pt-10 pb-20 px-6 scrollbar-none">
            <ProjectSelector activeProject={activeProject} />

            {activeProject.sidebar.map((section, idx) => (
              <div key={idx} className="mb-10 last:mb-0">
                <h3 className="sidebar-section mb-4 text-slate-500 font-black">{section.title}</h3>
                <nav className="space-y-1.5">
                  {section.items.map((item, idy) => (
                    <NavLink
                      key={idy}
                      to={item.path}
                      end={item.path === `/${activeProject.id}`}
                      onClick={() => setSidebarOpen(false)}
                      className={({ isActive }) => cn(
                        "flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-all group relative",
                        isActive 
                          ? "bg-white text-slate-900 font-bold shadow-md shadow-slate-200/50 ring-1 ring-slate-200/50" 
                          : "text-slate-600 hover:bg-white hover:text-slate-950 border border-transparent hover:border-slate-200/40 hover:shadow-sm"
                      )}
                    >
                      {({ isActive }) => (
                        <>
                          <item.icon size={18} className={cn("transition-colors", isActive ? "text-primary" : "group-hover:text-primary")} />
                          {item.name}
                          {isActive && (
                            <div className="absolute left-0 w-1.5 h-6 bg-primary rounded-r-full shadow-[0_0_8px_rgba(255,107,0,0.3)]" />
                          )}
                        </>
                      )}
                    </NavLink>
                  ))}
                </nav>
              </div>
            ))}
          </div>
        </aside>

        {/* Content Area */}
        <main className="flex-1 overflow-y-auto overflow-x-hidden relative scroll-smooth bg-white">
          <div className="max-w-[85ch] mx-auto py-20 px-8 lg:px-16 min-h-full pb-32">
            {children}
            <footer className="mt-40 py-12 border-t border-border flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-slate-400 font-medium">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-emerald-500 shadow-sm" />
                <span>All systems operational</span>
              </div>
              <span>© 2026 Cyxor Labs. Distributed Intelligence.</span>
            </footer>
          </div>
          {/* Subtle background glow */}
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-[120px] pointer-events-none -z-10" />
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-blue-500/5 rounded-full blur-[120px] pointer-events-none -z-10" />
        </main>
      </div>
    </div>
  );
};
